import { RATE_LIMITED_MESSAGE } from './trpc';

// DDoS mode (.specs/ddos-mode.md): the server refuses everything but sign-in to
// a logged-out caller, and everything but verifying to a signed-in one who has
// not proven they are human recently. This file is the one definition of the
// codes and the status shape, shared by the server and every client.

/**
 * The two refusals. Each travels as the tRPC error's whole `message` with code
 * `FORBIDDEN`, the same way `ACTIVE_SESSION_SWITCHED` and `CAPTCHA_REQUIRED`
 * do: @factiii/auth owns the tRPC error formatter and passes on only `code`,
 * `httpStatus`, `path` and `zodError`, so there is no other field a server
 * error can carry to a client. Compare with `ddosRefusalOf`, never a substring.
 */
export const DDOS_LOGIN_REQUIRED = 'DDOS_LOGIN_REQUIRED';
export const DDOS_HUMAN_VERIFICATION_REQUIRED =
  'DDOS_HUMAN_VERIFICATION_REQUIRED';

export type DdosRefusal =
  | typeof DDOS_LOGIN_REQUIRED
  | typeof DDOS_HUMAN_VERIFICATION_REQUIRED;

/** HTTP status both refusals answer with. Not 401: a 401 is how every client
 *  decides a session is over, and neither refusal ends one. */
export const DDOS_REFUSAL_STATUS = 403;
const DDOS_REFUSAL_TRPC_CODE = 'FORBIDDEN';

/** What this caller may do right now. */
export type DdosAccess =
  /** Everything they could do with the mode off. */
  | 'open'
  /** Logged out while the mode is on: only sign-in, sign-up and recovery. */
  | 'loginRequired'
  /** Signed in, but not verified as human recently enough: only verifying. */
  | 'humanVerificationRequired';

/** Answer of the public `ddosStatus` query. */
export type DdosStatus = {
  ddosMode: boolean;
  access: DdosAccess;
};

/** What a client assumes until the server has answered, and when it cannot. */
export const DDOS_STATUS_OPEN: DdosStatus = { ddosMode: false, access: 'open' };

/**
 * Header the Next.js server sends on its own anonymous calls (SSR, ISR, OG
 * images, sitemaps) so they are not refused as a logged-out visitor. The value
 * is the `WEB_SERVER_SECRET` env var, set on both the web and the API
 * deployment. Server-side only: it must never reach a browser bundle.
 */
export const WEB_SERVER_SECRET_HEADER = 'x-factiii-web-secret';

type ErrorLike = {
  message?: unknown;
  data?: { code?: unknown } | null;
};

/** Which DDoS refusal a thrown tRPC call is, or null when it is anything else. */
export const ddosRefusalOf = (error: unknown): DdosRefusal | null => {
  if (typeof error !== 'object' || error === null) return null;
  const { message, data } = error as ErrorLike;
  if (data?.code !== DDOS_REFUSAL_TRPC_CODE) return null;
  if (message === DDOS_LOGIN_REQUIRED) return DDOS_LOGIN_REQUIRED;
  if (message === DDOS_HUMAN_VERIFICATION_REQUIRED)
    return DDOS_HUMAN_VERIFICATION_REQUIRED;
  return null;
};

/** The refusal a status maps to, for clients that route on either. */
export const refusalForAccess = (access: DdosAccess): DdosRefusal | null => {
  if (access === 'loginRequired') return DDOS_LOGIN_REQUIRED;
  if (access === 'humanVerificationRequired')
    return DDOS_HUMAN_VERIFICATION_REQUIRED;
  return null;
};

// ---------------------------------------------------------------------------
// A refused server render. The Next.js servers call the API with no session;
// with the mode on and no valid `WEB_SERVER_SECRET` those calls are refused
// like any logged-out visitor's. A render must tell that apart from "this
// does not exist": a refusal rendered as not-found reaches crawlers as a
// noindex page, and cached, it outlives the attack.
// ---------------------------------------------------------------------------

/**
 * `digest` of the error a refused server render throws. It is the digest, not
 * the class or the message, that identifies it: a throw inside a `'use cache'`
 * function reaches its caller through React's serializer, which in a
 * production build drops the message and the prototype and keeps only an
 * error's own `digest` (the same way `notFound()` crosses that boundary).
 */
export const RENDER_REFUSED_DIGEST = 'FACTIII_RENDER_REFUSED';

/**
 * A server render the global rate limiter answered 429. All logged-out renders
 * share the web server's address, so a burst of them was limited together
 * (the API now exempts the web secret, see `isUnlimitedWebServerRequest`), and
 * a 429 read as "no data" rendered as not found. It says no more about whether
 * the thing exists than a DDoS refusal does, so it is one.
 */
export const RENDER_RATE_LIMITED = 'RENDER_RATE_LIMITED';

/** Why the API refused a server render. */
export type RenderRefusal = DdosRefusal | typeof RENDER_RATE_LIMITED;

/** Whether a thrown tRPC call is the limiter's 429: the fetch wrappers throw
 *  `RATE_LIMITED_MESSAGE` with no `data`, a parsed reply carries the status. */
const isRateLimited = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) return false;
  const { message, data } = error as ErrorLike & {
    data?: { httpStatus?: unknown } | null;
  };
  return message === RATE_LIMITED_MESSAGE || data?.httpStatus === 429;
};

/** Thrown in place of the tRPC error when the API refuses a server render. */
export class RenderRefusedError extends Error {
  readonly digest = RENDER_REFUSED_DIGEST;
  constructor(readonly refusal: RenderRefusal) {
    super(`The API refused a server render: ${refusal}`);
    this.name = 'RenderRefusedError';
  }
}

/** Whether a caught error is a refused server render, on either side of a
 *  `'use cache'` boundary. */
export const isRenderRefused = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { digest?: unknown }).digest === RENDER_REFUSED_DIGEST;

/** Rethrows a DDoS refusal or a 429 as `RenderRefusedError`; anything else
 *  unchanged. */
export const throwIfRenderRefused = (error: unknown): void => {
  const refusal = ddosRefusalOf(error);
  if (refusal) throw new RenderRefusedError(refusal);
  if (isRateLimited(error)) throw new RenderRefusedError(RENDER_RATE_LIMITED);
};

/**
 * `Retry-After` on the 503 a refused render answers, in seconds. A crawler
 * keeps what it has indexed and comes back; five minutes is also how long the
 * pages cache their API reads, so it never asks sooner than it would matter.
 */
export const RENDER_REFUSED_RETRY_AFTER_SECONDS = 300;

/** Headers of that 503: come back later, and store nothing. */
export const renderRefusedHeaders = (): Record<string, string> => ({
  'Retry-After': String(RENDER_REFUSED_RETRY_AFTER_SECONDS),
  'Cache-Control': 'no-store'
});

/**
 * Cache tags of the reads that are allowed to hold a fallback: the ones a
 * page is prerendered from at build time, where a refusal must not fail the
 * deploy. The API revalidates them when `ddosMode` is turned off, so a build
 * made during the attack does not keep its fallback. Every other cached read
 * throws on a refusal, and a throw is never cached.
 */
export const PUBLIC_STATS_CACHE_TAG = 'stats:public';
export const RENDER_FALLBACK_CACHE_TAGS: readonly string[] = [
  PUBLIC_STATS_CACHE_TAG
];
